import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import api from "../api/axios";
import { useSnackbar } from "notistack";
import FinancePageHeader from "../components/finance/FinancePageHeader";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { useAuth } from "../context/AuthContext";

const MetricCard = ({ title, value, subtitle }) => (
  <Card
    sx={{
      borderRadius: 3,
      height: "100%",
      border: "1px solid",
      borderColor: "divider",
      boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
    }}
  >
    <CardContent>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800 }}>
        {value}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      ) : null}
    </CardContent>
  </Card>
);

const formatMoney = (value, currency) => {
  const amount = Number(value || 0);
  return `${amount.toLocaleString()} ${currency}`;
};

const currencyValue = (obj, currency) => Number(obj?.[currency] || 0);

const FinancialDashboardPage = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/dashboard/financial-summary");
      setData(res.data);
    } catch (err) {
      enqueueSnackbar("Échec du chargement du dashboard financier", {
        variant: "error",
      });
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const cashboxes = useMemo(() => data?.cashboxes || [], [data]);
  const alerts = useMemo(() => data?.alerts || [], [data]);
  const recentMovements = useMemo(() => data?.recent_movements || [], [data]);
  const recentLoans = useMemo(() => data?.recent_loans || [], [data]);
  const today = useMemo(() => data?.today || {}, [data]);
  const loans = useMemo(() => data?.loans || {}, [data]);

  // Never combine different currencies into one financial amount.
  // Use backend per-currency aggregates when available.
  const loanTotalsByCurrency = useMemo(
    () => loans.by_currency || loans.byCurrency || {},
    [loans]
  );

  const usdCash = useMemo(
    () => cashboxes.find((c) => c.currency === "USD")?.current_balance || 0,
    [cashboxes]
  );
  const cdfCash = useMemo(
    () => cashboxes.find((c) => c.currency === "CDF")?.current_balance || 0,
    [cashboxes]
  );
  const rmbCash = useMemo(
    () => cashboxes.find((c) => c.currency === "RMB")?.current_balance || 0,
    [cashboxes]
  );

  return (
    <DashboardLayout
      role={user?.role}
      activePage="/financial-dashboard"
      title="Tableau de bord financier"
    >
      <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
        <Stack spacing={2.5}>
          <FinancePageHeader
            title="Tableau de bord financier"
            subtitle={
              data?.agency
                ? `${data.agency.name} • ${data.agency.city || ""} ${data.agency.country || ""}`
                : "Vue consolidée des caisses, activités et prêts de l’agence."
            }
          />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : !data ? (
            <Alert severity="warning" sx={{ borderRadius: 3 }}>
              Aucune donnée disponible.
            </Alert>
          ) : (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <MetricCard
                    title="Caisse USD"
                    value={formatMoney(usdCash, "USD")}
                    subtitle="Solde actuel"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MetricCard
                    title="Caisse CDF"
                    value={formatMoney(cdfCash, "CDF")}
                    subtitle="Solde actuel"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MetricCard
                    title="Caisse RMB"
                    value={formatMoney(rmbCash, "RMB")}
                    subtitle="Solde actuel"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <MetricCard
                    title="Prêts actifs"
                    value={String(loans.active_count || 0)}
                    subtitle="Nombre de prêts en cours"
                  />
                </Grid>
                {["USD", "CDF", "RMB"].map((currency) => {
                  const totals = loanTotalsByCurrency?.[currency];
                  if (!totals) return null;

                  const remaining =
                    totals.amount_remaining ??
                    totals.total_amount_remaining ??
                    totals.remaining ??
                    0;

                  return (
                    <Grid item xs={12} md={4} key={currency}>
                      <MetricCard
                        title={`À recouvrer — ${currency}`}
                        value={formatMoney(remaining, currency)}
                        subtitle="Solde restant des prêts actifs"
                      />
                    </Grid>
                  );
                })}
              </Grid>

              {alerts.length > 0 && (
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                    Alertes de caisse
                  </Typography>

                  <Stack spacing={1}>
                    {alerts.map((alert, idx) => (
                      <Alert
                        key={`${alert.currency}-${idx}`}
                        severity={alert.severity === "critical" ? "error" : "warning"}
                        sx={{ borderRadius: 2 }}
                      >
                        {alert.message} — Solde actuel :{" "}
                        <strong>{formatMoney(alert.current_balance, alert.currency)}</strong>
                      </Alert>
                    ))}
                  </Stack>
                </Paper>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                      Activité du jour
                    </Typography>

                    <Stack spacing={1}>
                      <Typography>
                        Approvisionnements :{" "}
                        <strong>
                          USD {currencyValue(today.fundings, "USD").toLocaleString()} | CDF{" "}
                          {currencyValue(today.fundings, "CDF").toLocaleString()} | RMB{" "}
                          {currencyValue(today.fundings, "RMB").toLocaleString()}
                        </strong>
                      </Typography>

                      <Typography>
                        Dépenses :{" "}
                        <strong>
                          USD {currencyValue(today.expenses, "USD").toLocaleString()} | CDF{" "}
                          {currencyValue(today.expenses, "CDF").toLocaleString()} | RMB{" "}
                          {currencyValue(today.expenses, "RMB").toLocaleString()}
                        </strong>
                      </Typography>

                      <Typography>
                        Salaires :{" "}
                        <strong>
                          USD {currencyValue(today.salaries, "USD").toLocaleString()} | CDF{" "}
                          {currencyValue(today.salaries, "CDF").toLocaleString()} | RMB{" "}
                          {currencyValue(today.salaries, "RMB").toLocaleString()}
                        </strong>
                      </Typography>

                      <Typography>
                        Remboursements prêts :{" "}
                        <strong>
                          USD {currencyValue(today.repayments, "USD").toLocaleString()} | CDF{" "}
                          {currencyValue(today.repayments, "CDF").toLocaleString()} | RMB{" "}
                          {currencyValue(today.repayments, "RMB").toLocaleString()}
                        </strong>
                      </Typography>

                      <Typography>
                        Retraits transactions :{" "}
                        <strong>
                          USD {currencyValue(today.payouts, "USD").toLocaleString()} | CDF{" "}
                          {currencyValue(today.payouts, "CDF").toLocaleString()} | RMB{" "}
                          {currencyValue(today.payouts, "RMB").toLocaleString()}
                        </strong>
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                      Caisses par devise
                    </Typography>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Devise</TableCell>
                            <TableCell>Solde</TableCell>
                            <TableCell>Seuil minimum</TableCell>
                            <TableCell>Statut</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cashboxes.map((row) => (
                            <TableRow key={row.id} hover>
                              <TableCell>{row.currency}</TableCell>
                              <TableCell>
                                {formatMoney(row.current_balance, row.currency)}
                              </TableCell>
                              <TableCell>
                                {formatMoney(row.minimum_alert_balance, row.currency)}
                              </TableCell>
                              <TableCell>{row.is_active ? "Active" : "Inactive"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                      Derniers mouvements de caisse
                    </Typography>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Sens</TableCell>
                            <TableCell>Montant</TableCell>
                            <TableCell>Devise</TableCell>
                            <TableCell>Par</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {recentMovements.map((row) => (
                            <TableRow key={row.id} hover>
                              <TableCell>{new Date(row.created_at).toLocaleString()}</TableCell>
                              <TableCell>{row.movement_type}</TableCell>
                              <TableCell>{row.direction}</TableCell>
                              <TableCell>{row.amount}</TableCell>
                              <TableCell>{row.currency}</TableCell>
                              <TableCell>{row.created_by_name || "-"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                      Derniers prêts
                    </Typography>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Débiteur</TableCell>
                            <TableCell>Initial</TableCell>
                            <TableCell>Reste</TableCell>
                            <TableCell>Statut</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {recentLoans.map((row) => (
                            <TableRow key={row.id} hover>
                              <TableCell>{row.debtor_name || "-"}</TableCell>
                              <TableCell>
                                {row.principal_amount} {row.currency}
                              </TableCell>
                              <TableCell>
                                {row.amount_remaining} {row.currency}
                              </TableCell>
                              <TableCell>{row.status}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </Stack>
      </Container>
    </DashboardLayout>
  );
};

export default FinancialDashboardPage;